// «فترة مقفلة» — the global refusal dialog (board accounts/close--locked,
// 440 px, radius 22; full screen under 768 px). Two exits exactly as drawn:
//   coral «قيد عكس بتاريخ اليوم»  → a reversal entry dated today (open period)
//   outline-teal «طلب إعادة الفتح» → for EVERY role while the problem says
//                                    reopen_request_available: the owner goes
//                                    to the period page's direct reopen (reason,
//                                    logged); staff / manager write a reason in
//                                    the dialog and erp-core POSTs
//                                    /periods/{m}/reopen-requests to the owner
//                                    (toast «أُرسل الطلب إلى المالك»)
//   text «إلغاء» (also Esc / scrim)
// The body is the server's Arabic sentence (HANDOFF §7 «{المستند} بتاريخ
// {التاريخ}، و{الشهر} مقفل منذ {التاريخ} ({من أقفله}). لا يمكن تعديلها.»)
// followed by the board's second sentence; document numbers stay LTR.
//
// A NEW document (nothing posted to reverse) may bring its own coral instead:
// `redate` («احفظه بتاريخ اليوم» …), which closes the dialog and re-submits.
//
// THE one implementation: <LockedPeriodHost/> is mounted ONCE by the app
// shell (AppShell) and the QueryClient's MutationCache sends every mutation
// refused with 409 `period_closed` to it (App.tsx); entry points that catch
// their own errors call showLockedPeriod(err, { reverse | redate }). No module
// keeps a local copy.

import { useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { Button, Dialog, PrimaryAction, useOnline, useToast } from '@/components';
import { useErpOptional } from '@/app/erp-context';
import { isApiError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { CLOSE_COPY, LOCKED_COPY, monthOnly } from './copy';
import { journalApi, useRequestReopen } from './data';
import { ReasonField } from './ReasonField';
import { isolateCodes, isolateText } from './isolate';
import {
  journalEntryToReverse,
  lockedPeriodStore,
  showLockedPeriod,
  type LockedPeriodOptions,
  type LockedPeriodRequest,
  type ReversalOutcome,
} from './locked-store';
import { useStableCallback } from './stable';
import type { PeriodClosedProblem } from './types';
import './close.css';

/** The board's first sentence, built from the extensions when the problem carries no `detail`. */
export function fallbackDetail(p: PeriodClosedProblem): string {
  const doc = [p.document_word_ar || 'المستند', p.document_no_display].filter(Boolean).join(' ');
  const date = p.document_date ? formatDate(p.document_date) : '';
  const month = p.period_month ? monthOnly(p.period_month) : '';
  const since = p.closed_at ? formatDate(p.closed_at) : '';
  const who = p.closed_by_name || p.closed_by;
  return `${doc} بتاريخ ${date}، و${month} مقفل منذ ${since}${who ? ` (${who})` : ''}. لا يمكن تعديلها.`;
}

/**
 * The board's second sentence names ONLY the exits the dialog draws (never
 * «اطلب من المالك» without a reopen exit for this person, never «أنشئ قيد عكس»
 * without the coral); no exit at all → the server's sentence stands alone.
 */
export function fixSentence(reversal: boolean, request: boolean): string | null {
  if (reversal && request) return LOCKED_COPY.fix;
  if (reversal) return LOCKED_COPY.fixReverseOnly;
  if (request) return LOCKED_COPY.fixRequestOnly;
  return null;
}

function reverseFor(req: LockedPeriodRequest): (() => Promise<ReversalOutcome | unknown>) | null {
  if (!req.problem.reversal_available) return null;
  if (req.reverse) return req.reverse;
  const entryId = journalEntryToReverse(req.problem);
  return entryId ? () => journalApi.reverse(entryId) : null;
}

function errorLine(e: unknown): string {
  if (isApiError(e) && !e.isNetwork && e.detail) return e.detail;
  return LOCKED_COPY.failed;
}

export interface LockedPeriodDialogProps {
  request: LockedPeriodRequest;
  onClose: () => void;
}

export function LockedPeriodDialog({ request, onClose }: LockedPeriodDialogProps) {
  const erp = useErpOptional();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const online = useOnline();
  const offline = !online;
  const p = request.problem;
  const isOwner = erp?.role === 'owner';
  const reverse = reverseFor(request);
  const redate = reverse ? null : (request.redate ?? null);
  const reopenable = p.reopen_request_available && !!p.period_month;
  // Every role: the owner reopens on the period page, the others send a request (ledger-bank-close-3).
  const canRequest = reopenable && !!erp;
  const requestM = useRequestReopen();

  const [busy, setBusy] = useState<'reverse' | 'request' | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [step, setStep] = useState<'refuse' | 'request'>('refuse');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const close = useStableCallback(() => {
    if (busy) return;
    onClose();
  });

  const doReverse = async () => {
    if (!reverse) return;
    setBusy('reverse');
    setFailure(null);
    try {
      const outcome = await reverse();
      const no = (outcome as ReversalOutcome | null)?.entry_no_display;
      setBusy(null);
      onClose();
      toast(isolateText(LOCKED_COPY.reversedToast(no)), { tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['erp'] });
      request.onReversed?.(outcome);
    } catch (e) {
      setBusy(null);
      // A reversal dated today refused again (today's month closed) re-opens the dialog for it.
      if (showLockedPeriod(e, { reverse: request.reverse, onReversed: request.onReversed })) return;
      setFailure(errorLine(e));
    }
  };

  const onRequest = () => {
    if (isOwner) {
      // The owner reopens on the period page (reason + audit), not from inside another screen.
      onClose();
      navigate(`/accounts/close?period=${p.period_month}&reopen=1`);
      return;
    }
    setFailure(null);
    setStep('request');
  };

  const sendRequest = () => {
    const why = reason.trim();
    if (!why) {
      setReasonError(CLOSE_COPY.reasonRequired);
      return;
    }
    setReasonError(null);
    setFailure(null);
    setBusy('request');
    requestM.mutate(
      { month: p.period_month as string, reason: why },
      {
        onSuccess: (r) => {
          setBusy(null);
          onClose();
          toast(r.already_requested ? LOCKED_COPY.alreadyRequestedToast : LOCKED_COPY.requestedToast);
        },
        onError: (e) => {
          setBusy(null);
          const field = isApiError(e) ? e.errors?.find((f) => f.field === 'reason') : undefined;
          if (field) setReasonError(field.message ?? CLOSE_COPY.reasonRequired);
          else setFailure(errorLine(e));
        },
      },
    );
  };

  const detail = request.error.detail || fallbackDetail(p);

  return (
    <Dialog
      open
      title={LOCKED_COPY.title}
      tone="danger"
      icon={<Lock aria-hidden="true" />}
      onClose={close}
      data-testid="locked-period-dialog"
      actions={
        step === 'request' ? (
          <>
            <PrimaryAction size="lg" block loading={busy === 'request'} disabled={offline} onClick={sendRequest} data-testid="reopen-request-send">
              {LOCKED_COPY.requestSend}
            </PrimaryAction>
            <Button variant="ghost" size="lg" block disabled={!!busy} onClick={() => setStep('refuse')}>
              {LOCKED_COPY.back}
            </Button>
          </>
        ) : (
        <>
          {reverse && (
            <PrimaryAction size="lg" block loading={busy === 'reverse'} disabled={offline} onClick={() => void doReverse()}>
              {LOCKED_COPY.reverse}
            </PrimaryAction>
          )}
          {redate && (
            <PrimaryAction
              size="lg"
              block
              disabled={!!busy}
              onClick={() => {
                onClose();
                redate.run();
              }}
            >
              {redate.label}
            </PrimaryAction>
          )}
          <div className="lck-row">
            {canRequest && (
              <Button variant="teal-line" size="lg" disabled={offline || !!busy} onClick={onRequest}>
                {LOCKED_COPY.request}
              </Button>
            )}
            <Button variant="ghost" size="lg" disabled={!!busy} onClick={close}>
              {LOCKED_COPY.cancel}
            </Button>
          </div>
        </>
        )
      }
    >
      {step === 'request' ? (
        <div className="lck-body" data-step="request">
          <p>{LOCKED_COPY.requestBody(p.period_month as string)}</p>
          <ReasonField value={reason} onChange={setReason} error={reasonError} disabled={busy === 'request'} autoFocus />
          {offline && <p className="lck-note">{LOCKED_COPY.offline}</p>}
          {failure && (
            <p className="lck-failure" role="alert">
              {failure}
            </p>
          )}
        </div>
      ) : (
      <div className="lck-body" data-step="refuse">
        <p data-testid="locked-period-detail">
          {isolateCodes(detail)} {fixSentence(!!reverse || redate?.label === LOCKED_COPY.reverse, canRequest)}
        </p>
        {offline && <p className="lck-note">{LOCKED_COPY.offline}</p>}
        {failure && (
          <p className="lck-failure" role="alert">
            {failure}
          </p>
        )}
      </div>
      )}
    </Dialog>
  );
}

/**
 * Renders the dialog for the current `lockedPeriodStore` request. Mount ONCE
 * at the app shell (inside Router + ToastProvider + QueryClientProvider);
 * extra hosts (a screen's fallback) stay silent while an earlier one lives.
 */
export function LockedPeriodHost() {
  const [hostId, setHostId] = useState<number | null>(null);
  useEffect(() => {
    const id = lockedPeriodStore.registerHost();
    setHostId(id);
    return () => lockedPeriodStore.unregisterHost(id);
  }, []);
  const request = useSyncExternalStore(lockedPeriodStore.subscribe, lockedPeriodStore.get, lockedPeriodStore.get);
  const active = useSyncExternalStore(lockedPeriodStore.subscribe, lockedPeriodStore.activeHost, lockedPeriodStore.activeHost);
  if (!request || hostId == null || active !== hostId) return null;
  return <LockedPeriodDialog key={request.id} request={request} onClose={lockedPeriodStore.close} />;
}

/**
 * Hook form for entry points that catch their own errors:
 *   const { handle } = usePeriodClosed();
 *   onError: (e) => { if (handle(e, { reverse: () => invoicesApi.reverse(id) })) return; … }
 */
export function usePeriodClosed(): { handle: (error: unknown, opts?: LockedPeriodOptions) => boolean } {
  return { handle: showLockedPeriod };
}
